/**
 * account.tryairlock.app — the member area.
 *
 * Two shells: the signed-out screen, and the signed-in app. Which one is on
 * screen is decided by a single question asked of the server — `GET /api/me` —
 * rather than by anything held in the browser, because a page that decides for
 * itself whether somebody is signed in will eventually disagree with the
 * Worker that actually enforces it.
 *
 * Signed in, there are four shells and not one, and which is open is a fact
 * about *where you are* — see `components/nav.ts`. This file holds the state
 * that answers it: the organization being looked at, the workspace entered,
 * the page and the section. Everything else here is unchanged: the query
 * string is read once, and each of its four entries is spent and cleaned away.
 *
 *   ?checkout=  back from Stripe — confirmed against Stripe, then cleaned away
 *   ?verify=    the link out of a confirmation email. Spent before anything
 *               else is decided, and it works signed out — the token proves the
 *               address, not the browser
 *   ?invite=    an invitation — a sign-up screen that names the organization,
 *               or a banner offering to accept
 *   ?device=    a machine asking for this account's licence key, which takes
 *               over the main column rather than sitting in a strip: it is the
 *               one screen here that hands a credential to something outside
 *               the browser, and it should be the only thing being decided
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { color, font } from './theme/tokens';
import { PageHeader, Rail, type CrumbTrail } from './components/Shell';
import { RailDrawer } from './components/RailDrawer';
import { BillingBanner } from './components/BillingBanner';
import { TrialBanner } from './components/TrialBanner';
import { ConfirmStrip, InviteBar } from './components/ConfirmStrip';
import { Note } from './components/list';
import {
  allowedIn,
  firstSection,
  landingOf,
  menuPagesOf,
  orgRail,
  personalRail,
  systemRail,
  titleOf,
  workspaceRail,
  type PageId,
  type RailState,
  type ShellKind
} from './components/nav';
import { BootLoading, useLoadPhase } from './components/Loading';
import { Auth } from './pages/Auth';
import { LinkMachine } from './pages/LinkMachine';
import { Router } from './pages/Router';
import { OpenInAirlock } from './pages/cloud/OpenInAirlock';
import { seatOrgs } from './pages/cloud/shared';
import { api, ApiError, SIGNED_OUT, apiPath } from './lib/api';
import { deviceParam, tokenParam } from './lib/api-path';
import { useIdleSignOut } from './lib/idle';
import { pathOf, routeOf, type Route } from './lib/route';
import { billingTrouble, trialNotice } from './lib/billing';
import type { Config, Me, Organization, OrgSettingsRecord, WorkspaceList, WorkspaceRight, WorkspaceSummary } from './lib/types';

/* ── where you are ─────────────────────────────────────────────────────── */

interface Where {
  page: PageId;
  /** The child of a section, for the pages that have children. */
  section: string | null;
  /** The organization being looked at, `null` for a personal account. */
  orgId: string | null;
  /** Whether a workspace has been entered — which is what replaces the rail. */
  inWorkspace: boolean;
  /** Which one, `null` while one is being started rather than opened. */
  workspaceId: string | null;
}

const START: Where = { page: 'overview', section: null, orgId: null, inWorkspace: false, workspaceId: null };

/**
 * Which shell `where` describes.
 *
 * Order matters and says what is nested in what: inside a workspace you are in
 * that workspace and nothing else, and the deployment's pages are their own
 * place rather than a fourth group in somebody's organization.
 *
 * The last line is the one exception to "which shell is open is never a
 * question of who you are": an address that administers the deployment has no
 * personal shell behind it. It buys no licence, is sent no invoice and
 * installs on no machine, so what would be under its own name is the
 * deployment — and a rail offering it `Licence` and `Invoices` would be three
 * rows of things this account cannot do.
 */
function shellOf(where: Where, systemAdmin: boolean): ShellKind {
  // The deployment's pages are a place only a system administrator has, so a
  // `#adminAccounts` typed by anybody else is a page their shell does not hold
  // — they land on their own, and not on a rail of rows the API answers 404.
  if (systemAdmin && where.page.startsWith('admin')) return 'system';
  if (where.inWorkspace) return 'workspace';
  if (where.orgId) return 'org';
  return systemAdmin ? 'system' : 'personal';
}

/**
 * What a page named in the address bar used to be called.
 *
 * The site's footer links here with `#downloads`, an old bookmark says
 * `#seats`, and a link that lands somewhere else is a link that does not work.
 * Kept as a table rather than quietly dropped: these are other people's URLs.
 */
const OLD_NAMES: Record<string, PageId> = {
  dashboard: 'overview',
  plan: 'licence',
  billing: 'invoices',
  devices: 'machines',
  org: 'billing',
  seats: 'members',
  projects: 'workspaces',
  project: 'workspaces',
  projectHistory: 'workspaces',
  orgSettings: 'orgSettings'
};

const NEW_NAMES: readonly string[] = [
  'overview',
  'licence',
  'invoices',
  'machines',
  'downloads',
  'profile',
  'affiliate',
  'workspaces',
  'teams',
  'members',
  'orgMachines',
  'audit',
  'usage',
  'runs',
  'spend',
  'streams',
  'billing',
  'orgSettings',
  'orgWorkflows',
  'orgRules',
  'orgAgents',
  'orgIntegrations',
  'orgSecrets',
  'orgSecurity',
  'adminOverview',
  'adminAccounts',
  'adminOrgs',
  'adminAffiliates',
  'adminRequests'
];

/** The page a `#fragment` names, if it names one this shell has. */
function openingPage(): PageId | null {
  const named = window.location.hash.replace(/^#/, '');
  if (!named) return null;
  return NEW_NAMES.includes(named) ? (named as PageId) : (OLD_NAMES[named] ?? null);
}

/**
 * The invitation token in the address bar, if this browser arrived with one —
 * and only in the shape the Worker issues: the value goes into an API path, and
 * `?invite=../billing/cancel%23` once took the accept call somewhere else
 * (0373/api-path-encoded). Anything else is no invitation.
 */
function inviteToken(): string | null {
  return tokenParam(window.location.search, 'invite');
}

/** The machine code in the address bar, if a desktop app sent the browser here. */
function deviceCode(): string | null {
  return deviceParam(window.location.search);
}

/** The confirmation token, if this browser arrived out of an email. */
function verifyToken(): string | null {
  return tokenParam(window.location.search, 'verify');
}

/** Whether this account administers the organization it is looking at. */
function administersHere(me: Me, orgId: string | null): boolean {
  const role = me.organizations.find((organization) => organization.id === orgId)?.role;
  return role === 'owner' || role === 'admin';
}

export default function App(): React.JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  // What the signed-out screen needs to know about this deployment. Signed in
  // it arrives inside `/me`; signed out there is nothing to attach it to, so it
  // is read on its own — which is also what hands the browser its CSRF cookie.
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  // The fragment is read once, not routed on. Nothing writes it back — the
  // rail is the navigation, and a URL that lied about which page is open would
  // be worse than one that says nothing.
  const [opening] = useState<PageId | null>(() => openingPage());
  const [where, setWhere] = useState<Where>(START);
  // The name of the workspace that is open. It only arrives with the document,
  // so the page that reads it hands it up for the breadcrumb.
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  // What the caller may do in the workspace they entered. Read off the list row
  // they opened, and again off the document once a page has read one — the
  // access page needs it to know whether to offer a control or a sentence.
  const [workspaceRight, setWorkspaceRight] = useState<WorkspaceRight | null>(null);
  // Which team the team page is about, and its name for the breadcrumb.
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teamName, setTeamName] = useState<string | null>(null);
  // Whether a member's overview is past its first day, as that page reports it
  // — `null` while it is still finding out. The header is the only thing here
  // that needs to know, and it needs it because the page has two names.
  const [memberSettled, setMemberSettled] = useState<boolean | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // The rail as a drawer under 1024 (§38.3).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [crumbOpen, setCrumbOpen] = useState(false);
  // Which account or organization the deployment's detail pages are about.
  const [inspecting, setInspecting] = useState<string | null>(null);
  // Which version of a workspace's history to open unfolded, when the audit
  // page is what sent somebody there.
  const [focusVersion, setFocusVersion] = useState<number | null>(null);
  // What the rail's state dots say: which agents this organization offers, and
  // which integrations it has turned on. Read once per organization, because a
  // dot that is only ever grey says nothing at all.
  const [railState, setRailState] = useState<RailState>({});
  // Bumped when the settings are published, so the rail follows what was just
  // decided: turning an integration off takes its row away, and a rail that
  // only noticed on the next reload would be a rail disagreeing with the page
  // that changed it.
  const [railTick, setRailTick] = useState(0);
  // An invitation accepted in this tab — which is the moment Airlock is worth
  // opening, since that organization's workspaces are now this account's too.
  const [joined, setJoined] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [invite, setInvite] = useState<string | null>(inviteToken);
  const [device, setDevice] = useState<string | null>(deviceCode);
  // Why the signed-out screen is on show, when it is not simply where somebody
  // arrived. A session that ends on its own has to say so: "sign in again" with
  // no reason reads as the site having lost the password, and the one reason
  // that matters here is the one nobody did on purpose.
  const [endedReason, setEndedReason] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setMe(await api.get<Me>('/me'));
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    void Promise.all([
      refresh(),
      api
        .get<Config>('/config')
        .then(setConfig)
        .catch(() => undefined)
    ]).finally(() => setLoading(false));
  }, [refresh]);

  // Out of a confirmation email. Spent once, signed in or not — the token is
  // what proves the address, and requiring a session as well would strand
  // somebody who signed up on a laptop and opened the mail on a phone. The
  // query string goes either way, so a reload cannot spend it twice.
  useEffect(() => {
    const token = verifyToken();
    if (!token) return;
    window.history.replaceState(null, '', window.location.pathname);
    void api
      .post<{ email: string }>('/auth/verify', { token })
      .then(async (answer) => {
        setBanner(`${answer.email} is confirmed. You can buy a licence and invite people to seats.`);
        await refresh();
      })
      .catch((failure) =>
        setBanner(
          failure instanceof ApiError
            ? failure.message
            : 'That confirmation link could not be used. Ask for a new one from your account.'
        )
      );
  }, [refresh]);

  // Back from Stripe. The redirect proves nothing on its own, so the session is
  // confirmed with Stripe before anything is claimed — and the query string is
  // dropped either way, so a reload cannot repeat it.
  useEffect(() => {
    const session = new URLSearchParams(window.location.search).get('checkout');
    if (!session) return;
    window.history.replaceState(null, '', window.location.pathname);
    void api
      .post<{ status: string; orgId?: string }>('/checkout/confirm', { session })
      .then(async (answer) => {
        setBanner(
          answer.status === 'ready'
            ? 'Thank you — your licence is active and your key is below.'
            : answer.status === 'seats'
              ? 'Thank you — your organization’s seats are active. Invite people from Members.'
              : 'Payment is still processing. Your key appears here as soon as Stripe confirms it.'
        );
        await refresh();
        if (answer.status === 'seats') {
          setWhere((current) => ({
            ...current,
            page: 'members',
            section: null,
            orgId: answer.orgId ?? current.orgId,
            inWorkspace: false,
            workspaceId: null
          }));
        }
      })
      .catch(() => setBanner('We could not confirm that payment. If you were charged, write to support@tryairlock.app.'));
  }, [refresh]);

  /**
   * Going somewhere.
   *
   * Leaving a workspace is a move to the organization's shell, so a page that
   * belongs to the organization clears the workspace rather than being drawn
   * inside one — the rail and the page would otherwise disagree about where
   * this person is.
   */
  useEffect(() => {
    const id = where.orgId;
    if (!id) {
      setRailState({});
      return;
    }
    let live = true;
    void api
      .get<OrgSettingsRecord>(apiPath`/orgs/${id}/settings`)
      .then((answer) => {
        if (!live) return;
        const on = { ...answer.settings.defaults, ...answer.settings.onboarding } as Record<string, unknown>;
        setRailState({
          agents: Object.fromEntries(
            (Array.isArray(on.agents) ? (on.agents as string[]) : ['claude-code', 'codex']).map((agent) => [agent, true])
          ),
          integrations: {
            jira: Boolean(on.jiraEnabled),
            trello: Boolean(on.trelloEnabled),
            // These two default to on in the app, so an organization that has
            // said nothing has them on — which is what the rail has to show.
            github: on.gitEnabled !== false,
            ngrok: Boolean(on.tunnelsEnabled),
            bookmarklet: on.bookmarkletEnabled !== false
          }
        });
      })
      .catch(() => setRailState({}));
    return () => {
      live = false;
    };
  }, [where.orgId, railTick]);

  const go = useCallback((page: PageId, section: string | null = null): void => {
    setWhere((current) => {
      const stays = current.inWorkspace && allowedIn('workspace', page);
      return {
        page,
        // A page that holds sections is never open on none of them: arriving
        // at Settings means arriving at Sessions, the way the rail shows it.
        section: section ?? firstSection(page),
        orgId: current.orgId,
        inWorkspace: stays,
        workspaceId: stays ? current.workspaceId : null
      };
    });
    setMenuOpen(false);
    setCrumbOpen(false);
    window.scrollTo(0, 0);
  }, []);

  /** Entering a workspace replaces the rail; `null` starts a new one. */
  const enterWorkspace = useCallback(
    (workspaceId: string | null, right: WorkspaceRight | null = null, name: string | null = null): void => {
      // The name comes off the row that was opened, so the breadcrumb is right
      // before any page has read the document — the access page never reads one.
      setWorkspaceName(name);
      setWorkspaceRight(right);
      setWhere((current) => ({ ...current, page: 'wsProjects', section: null, inWorkspace: true, workspaceId }));
      setMenuOpen(false);
      setCrumbOpen(false);
      window.scrollTo(0, 0);
    },
    []
  );

  const switchOrg = useCallback((orgId: string | null): void => {
    setWorkspaceName(null);
    setWhere({ ...START, page: 'overview', orgId });
    setMenuOpen(false);
    setCrumbOpen(false);
  }, []);

  const signOut = useCallback(async (reason: string | null = null): Promise<void> => {
    await api.post('/auth/signout').catch(() => undefined);
    setEndedReason(reason);
    setMe(null);
    setWhere(START);
    setMenuOpen(false);
  }, []);

  /**
   * A 401 from anywhere means the session is gone — idled out, expired, or
   * signed out in another tab — and the shell has to follow.
   *
   * It is listened for rather than checked, because the app is handed its
   * account in the sign-in response and never asks again on its own: until this
   * existed, a browser whose session had ended kept drawing a signed-in
   * dashboard in which every page said "Sign in first", which is the shape of
   * the bug this arrived with.
   */
  useEffect(() => {
    const ended = (): void => {
      setMe((current) => {
        if (current) setEndedReason('You were signed out. Sign in again to carry on.');
        return null;
      });
    };
    window.addEventListener(SIGNED_OUT, ended);
    return () => window.removeEventListener(SIGNED_OUT, ended);
  }, []);

  // Signed out after the deployment's window of doing nothing. The Worker drops
  // the session either way; this is what makes it visible on a tab nobody has
  // touched, instead of at the next click an hour later.
  const idled = useCallback((): void => {
    void signOut(`You were signed out after ${me?.config.idleMinutes ?? 60} minutes of inactivity.`);
  }, [signOut, me?.config.idleMinutes]);
  // Any authenticated read pushes the session's own clock forward; `/me` is the
  // cheapest, and refreshing what the shell is drawing is a second use for it.
  const heartbeat = useCallback((): void => void refresh(), [refresh]);
  useIdleSignOut({
    active: Boolean(me),
    minutes: me?.config.idleMinutes ?? 0,
    onHeartbeat: heartbeat,
    onIdle: idled
  });

  // The organization this shell opens on, once the account is known. Settled
  // here rather than at sign-in so that accepting an invitation in this tab
  // moves the shell into that organization without a reload.
  const orgs = useMemo(() => (me ? seatOrgs(me) : []), [me]);
  // The organization's workspaces, for the crumb's switcher inside one.
  const [siblings, setSiblings] = useState<WorkspaceSummary[]>([]);
  useEffect(() => {
    if (!where.inWorkspace || !where.orgId) return;
    let live = true;
    api
      .get<WorkspaceList>(apiPath`/orgs/${where.orgId}/workspaces`)
      .then((answer) => {
        if (!live) return;
        setSiblings(answer.workspaces);
        // A workspace reached by its address was opened from no row, so no
        // name came with it: the list names it, for the crumb and for what the
        // pages say of it (Access's Remove), before any page reads a document.
        const here = answer.workspaces.find((workspace) => workspace.id === where.workspaceId);
        if (here) setWorkspaceName((current) => current ?? here.name);
      })
      .catch(() => live && setSiblings([]));
    return () => {
      live = false;
    };
  }, [where.inWorkspace, where.orgId, where.workspaceId]);
  // The address bar names the place (README §2 bis of the redesign): read once
  // when the account is known, written on every move after, and read again on
  // Back and Forward.
  const routed = useRef(false);
  const fromPath = useRef(false);
  const settling = useRef(false);
  const applyRoute = useCallback((route: Route): void => {
    setTeamId(route.teamId);
    setInspecting(route.inspecting);
    setWhere((current) => ({
      page: route.page,
      section: route.section ?? firstSection(route.page),
      orgId: route.orgId === undefined ? current.orgId : route.orgId,
      inWorkspace: route.inWorkspace,
      workspaceId: route.workspaceId
    }));
  }, []);
  useEffect(() => {
    if (!me || routed.current) return;
    routed.current = true;
    // This pass still holds the place from before the path was read.
    settling.current = true;
    const route = routeOf(window.location.pathname, orgs);
    if (!route) return;
    fromPath.current = true;
    applyRoute({ ...route, orgId: route.orgId === undefined ? (orgs[0]?.id ?? null) : route.orgId });
  }, [me, orgs, applyRoute]);
  useEffect(() => {
    const back = (): void => {
      const route = routeOf(window.location.pathname, orgs);
      if (route) applyRoute(route);
    };
    window.addEventListener('popstate', back);
    return () => window.removeEventListener('popstate', back);
  }, [orgs, applyRoute]);
  const written = useRef(false);
  useEffect(() => {
    if (!me || !routed.current) return;
    if (settling.current) {
      settling.current = false;
      return;
    }
    const path = pathOf({ ...where, teamId, inspecting }, orgs);
    if (path === window.location.pathname) {
      written.current = true;
      return;
    }
    // The first write keeps the query a return carried (`?checkout=`, `?github=`)
    // for the page that reads it; every later one is a move of its own.
    if (written.current) window.history.pushState(null, '', path);
    else window.history.replaceState(null, '', path + window.location.search + window.location.hash);
    written.current = true;
  }, [me, orgs, where, teamId, inspecting]);
  // The account the shell has landed for. `me` is read again by every
  // heartbeat and every `refresh()`, and an account with no organization — a
  // system administrator, a personal licence — is on no organization by
  // design: without this, each new read sent it back to its landing, from the
  // middle of an inspection or a page it had chosen.
  const landedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!me) {
      landedFor.current = null;
      return;
    }
    const landed = landedFor.current === me.user.id;
    landedFor.current = me.user.id;
    setWhere((current) => {
      // A path that named a place has already settled it, an account with no
      // organization included.
      if (fromPath.current) {
        fromPath.current = false;
        return current;
      }
      if (orgs.some((organization) => organization.id === current.orgId)) return current;
      // Once landed, only an organization that is gone moves the shell.
      if (landed && current.orgId === null) return current;
      // A fragment names a page, never a place: it is spent on the first paint
      // that has an account to spend it against. With none, you open where your
      // account *is* — which for a system administrator is the deployment, and
      // not an overview of the licence it does not hold.
      const page = opening ?? (me.user.role === 'system_admin' ? 'adminOverview' : 'overview');
      // GitHub's return from installing the organization's app (`GithubAppPanel`)
      // lands on the GitHub section, where its outcome is said.
      const returning = page === 'orgIntegrations' && new URLSearchParams(window.location.search).has('github');
      return { ...START, page, section: returning ? 'github' : firstSection(page), orgId: orgs[0]?.id ?? null };
    });
  }, [me, orgs, opening]);

  const bootPhase = useLoadPhase(loading);

  // Nothing for 300 ms, then the mark, held 400 ms at least once shown (§37.2).
  if (bootPhase !== 'content') return <BootLoading shown={bootPhase === 'skeleton'} />;

  if (!me) {
    const query = new URLSearchParams(window.location.search);
    const intent = query.get('intent');
    return (
      <Auth
        // `?kind=org` comes off the marketing site's Team card. It preselects
        // the Organization half of the sign-up question rather than deciding
        // anything: the person can still switch, and the Worker reads the body
        // and not the query.
        initialKind={query.get('kind') === 'org' ? 'org' : 'personal'}
        // An invitation arrives at the sign-up half by default: whoever opens
        // one usually has no account yet, and the ones who do can switch. A
        // machine asking for a licence is the opposite — whoever owns that
        // licence has an account already. And a session that *ended* opens on
        // sign-in whatever brought this browser here originally.
        initialMode={!endedReason && (intent === 'signup' || (invite && !device)) ? 'signup' : 'signin'}
        invite={invite}
        device={device}
        siteUrl={config?.siteUrl ?? '/'}
        docsUrl={config?.docsUrl ?? 'https://docs.tryairlock.app'}
        githubSignIn={config?.githubSignIn ?? false}
        notice={endedReason}
        onSignedIn={(signedIn) => {
          setEndedReason(null);
          setMe(signedIn);
        }}
      />
    );
  }

  const systemAdmin = me.user.role === 'system_admin';
  const shell = shellOf(where, systemAdmin);
  // A page this shell does not hold is not drawn blank: it goes to the shell's
  // own landing, which is the same answer the API gives a route it refuses.
  // `#seats` typed into the address bar by a personal account lands on the
  // overview, and the Worker would have said 404 to the same request.
  const page: PageId = allowedIn(shell, where.page) ? where.page : landingOf(shell);
  const organization: Organization | null = orgs.find((one) => one.id === where.orgId) ?? null;
  const admin = administersHere(me, where.orgId);
  // At most two strips, in this order: payment, email, invitation, trial,
  // information (redesign, proposal 2) — a page under five strips is a page
  // nobody reads.
  const shownStrips = new Set(
    [
      !device && billingTrouble(me, organization, admin) ? 'billing' : null,
      !me.user.emailVerifiedAt ? 'email' : null,
      invite ? 'invite' : null,
      !device && page !== 'billing' && trialNotice(organization, admin) ? 'trial' : null,
      banner ? 'info' : null
    ]
      .filter((one): one is string => one !== null)
      .slice(0, 2)
  );

  /** Take the seat this browser arrived holding a token for. */
  const acceptInvite = async (): Promise<void> => {
    if (!invite) return;
    try {
      const answer = await api.post<{ organization: { name: string } }>(apiPath`/invites/${invite}/accept`);
      setBanner(`You now hold a seat in ${answer.organization.name}.`);
      setJoined(answer.organization.name);
      await refresh();
    } catch (failure) {
      setBanner(failure instanceof ApiError ? failure.message : 'That invitation could not be accepted.');
    } finally {
      setInvite(null);
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  /* ── the breadcrumb and the rail, per shell ─────────────────────────── */

  const orgPicks = orgs.map((one) => ({
    key: one.id,
    label: one.name,
    current: one.id === where.orgId,
    onPick: () => switchOrg(one.id)
  }));
  // A personal licence does not disappear because its holder later took a seat
  // somewhere, so the way back to it is offered wherever there is one to reach.
  // Never for a system administrator: there is no personal shell to go back
  // to, and the address is what the identity menu says, not a place.
  //
  // `orgId === null` is what makes it personal. `me.license` is the best row
  // this account holds, and a seat's own licence — a beta granted to one seat
  // of an organization — is one of them: a member who had never bought
  // anything was offered a shell of their own to switch to, which drew their
  // employer's grant as if it were theirs. The Worker draws the same line
  // wherever it asks the question (`licenses.ts`, `beta.ts`).
  const personalPick =
    orgs.length && !systemAdmin && (me.user.accountType === 'personal' || me.license?.orgId === null)
      ? // Named for the place it opens, not for who is signed in. The address
        // was the one label in the navigation that answered *who* rather than
        // *where* — and the identity menu at the other end of the header
        // already says it, under the initials that are its own control.
        [{ key: 'personal', label: 'Airlock', current: where.orgId === null, onPick: () => switchOrg(null) }]
      : [];

  // Inside a workspace the crumb switches workspaces (owner's decision, lot 3B):
  // the organization's open ones, then the archived ones, then all of them.
  const workspacePicks = [...siblings]
    .sort((a, b) => Number(Boolean(a.archivedAt)) - Number(Boolean(b.archivedAt)))
    .map((one) => ({
      key: one.id,
      label: one.archivedAt ? `${one.name} · archived` : one.name,
      current: one.id === where.workspaceId,
      onPick: () => enterWorkspace(one.id, one.access.right, one.name)
    }));

  const trail: CrumbTrail =
    shell === 'workspace' && organization
      ? {
          parent: { label: organization.name, onGo: () => go('workspaces') },
          here: workspaceName ?? (where.workspaceId ? '…' : 'New workspace'),
          locked: true,
          picks: workspacePicks.length > 0 ? workspacePicks : undefined,
          action: { label: `All of ${organization.name}’s workspaces`, onPick: () => go('workspaces') }
        }
      : page === 'team' && organization
        ? {
            parent: { label: organization.name, onGo: () => go('teams') },
            here: teamName ?? '…',
            action: { label: `All of ${organization.name}’s teams`, onPick: () => go('teams') }
          }
        : shell === 'system'
          ? {
              // The deployment is not somebody's account, so the crumb says the
              // product: the wordmark, as on a personal shell and on the
              // sign-in page. The address that signed in stays in the identity
              // menu, which is where a name says *who* rather than *where*.
              here: 'Airlock',
              wordmark: true,
              picks: orgPicks
            }
          : organization
            ? {
                // No verb under the rule. It offered `Billing & seats`, which
                // is a rail row two inches below it — a second door onto a page
                // that was already open to them, in the one control whose whole
                // job is to say *where you are*.
                here: organization.name,
                locked: true,
                picks: [...orgPicks, ...personalPick]
              }
            : {
                // A personal account is not a place with a name, so the crumb
                // says the product rather than the person: the email was the
                // only thing on screen that read like a label for where you are,
                // and it named who is signed in — which the identity menu at the
                // foot of the rail already says. No verb under the rule either:
                // "Start an organization" opened the licence page, which is not
                // where an organization is started (that is the profile's
                // "Account kind" card), so it was a door onto the wrong room.
                here: 'Airlock',
                wordmark: true,
                picks: orgPicks
              };

  // The crumb's selectors, stacked at the head of the drawer under 1024
  // (§38.3): the organization, then — inside one — the workspace.
  const drawerSelectors =
    organization && shell !== 'system'
      ? [
          { label: 'Organization', picks: [...orgPicks, ...personalPick] },
          ...(shell === 'workspace' && workspacePicks.length > 0 ? [{ label: 'Workspace', picks: workspacePicks }] : [])
        ]
      : [];

  const groups =
    shell === 'workspace'
      ? workspaceRail()
      : shell === 'system'
        ? systemRail()
        : organization
          ? orgRail(organization.name, admin, railState)
          : personalRail();

  return (
    <div
      className="al-shell"
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: color.shellPage,
        fontFamily: font.sans,
        color: color.ink
      }}
    >
      <Rail
        groups={groups}
        page={page}
        section={where.section}
        landing={landingOf(shell)}
        onGo={go}
      />
      <RailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        groups={groups}
        page={page}
        section={where.section}
        landing={landingOf(shell)}
        onGo={go}
        selectors={drawerSelectors}
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          trail={trail}
          crumbOpen={crumbOpen}
          onCrumbToggle={() => setCrumbOpen((open) => !open)}
          onCrumbClose={() => setCrumbOpen(false)}
          // A team's page has no rows, so its own name *is* the page's name.
          // A member's overview is the other exception: it is the one page in
          // the area addressed to a person rather than about a subject, and
          // "Overview" over three numbered acts reads as a summary of
          // something. Everywhere else the header names the page and leaves
          // the breadcrumb to name the place.
          //
          // Only over the acts, though. Once that member has a machine linked
          // and a workspace joined the page is their standing one, and a
          // welcome over it would greet somebody who arrived months ago.
          // `null` is "not yet known", and it reads as the ordinary title
          // rather than as a welcome: a greeting that appears and leaves again
          // is worse than one that arrives a moment late.
          title={
            page === 'team'
              ? (teamName ?? 'Team')
              : page === 'overview' && organization && !admin && memberSettled === false
                ? `Welcome to ${organization.name}`
                : titleOf(page, where.section)
          }
          user={me.user}
          menu={menuPagesOf(shell)}
          menuOpen={menuOpen}
          onToggleMenu={() => setMenuOpen((open) => !open)}
          onCloseMenu={() => setMenuOpen(false)}
          onGo={go}
          // Only from somewhere else: in the deployment's own shell the door
          // would open the page it is drawn on.
          onSystem={systemAdmin && shell !== 'system' ? () => go('adminOverview') : undefined}
          onSignOut={() => void signOut()}
          onDrawer={() => setDrawerOpen(true)}
        />

        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* At most two strips, in this order: payment, email, invitation,
              trial, information (redesign, proposal 2) — a page under five
              strips is a page nobody reads. */}
          {banner && shownStrips.has('info') && (
            <div style={{ padding: '16px var(--al-gutter) 0' }}>
              <Note tone="teal">{banner}</Note>
            </div>
          )}

          {!me.user.emailVerifiedAt && shownStrips.has('email') && (
            <div style={{ padding: '16px var(--al-gutter) 0' }}>
              <ConfirmStrip me={me} onRefresh={refresh} />
            </div>
          )}

          {invite && shownStrips.has('invite') && (
            <div style={{ padding: '16px var(--al-gutter) 0' }}>
              <InviteBar
                onAccept={() => void acceptInvite()}
                onDismiss={() => {
                  setInvite(null);
                  window.history.replaceState(null, '', window.location.pathname);
                }}
              />
            </div>
          )}

          {/* A failed charge or the grace week, for whoever pays — above every
              page, because the page somebody is on is rarely Billing. */}
          {!device && shownStrips.has('billing') && <BillingBanner me={me} organization={organization} admin={admin} />}

          {/* The organization trial, for whoever can buy out of it — Billing
              says it in its own card, so not there. */}
          {!device && page !== 'billing' && shownStrips.has('trial') && (
            <TrialBanner organization={organization} admin={admin} onBuy={() => go('billing')} />
          )}

          {joined && !device && (
            <div style={{ padding: '16px var(--al-gutter) 0' }}>
              <OpenInAirlock
                title={`Open Airlock — ${joined}’s workspaces are waiting`}
                onDownloads={() => go('downloads')}
              />
            </div>
          )}

          {/* A machine waiting on an answer is the only thing on screen while
              it waits: the decision is about a credential leaving the
              browser, and a page carrying on underneath would make it look
              routine. */}
          {device ? (
            <div style={{ padding: '20px var(--al-gutter) 30px' }}>
              <LinkMachine
                me={me}
                code={device}
                onDone={() => {
                  setDevice(null);
                  window.history.replaceState(null, '', window.location.pathname);
                }}
              />
            </div>
          ) : (
            <Router
              page={page}
              section={where.section}
              me={me}
              organization={organization}
              admin={admin}
              workspaceId={where.workspaceId}
              workspaceRight={workspaceRight}
              workspaceName={workspaceName}
              teamId={teamId}
              starting={where.inWorkspace && where.workspaceId === null}
              accountId={inspecting}
              focusVersion={focusVersion}
              onGo={go}
              onEnterWorkspace={enterWorkspace}
              onWorkspaceName={setWorkspaceName}
              onOpenTeam={(id) => {
                setTeamId(id);
                setTeamName(null);
                go('team');
              }}
              onTeamName={setTeamName}
              onMemberSettled={setMemberSettled}
              onInspectAccount={(id) => {
                setInspecting(id);
                go(page === 'adminOrgs' ? 'adminOrgDetail' : 'adminAccountDetail');
              }}
              onFocusVersion={setFocusVersion}
              onSettingsPublished={() => setRailTick((tick) => tick + 1)}
              onRefresh={refresh}
              onSignedOut={() => void signOut()}
            />
          )}
        </main>
      </div>
    </div>
  );
}
